<template>
  <div class="fts-list-view" ref="ftsListView" @scroll="onScroll">
    <!-- header -->
    <div class="list-header" :class="{ scrolled: scroll }">
      <div class="list-nav">
        <div class="list-nav-l">
          <!-- menu -->
          <fts-label-group
            :item="list"
            emoji
            :emoji-size="20"
            icon-class="list-nav-menu-emoji"
            label-class="list-nav-menu-name"
            v-if="list.id"
          />
        </div>
        <div class="list-nav-c">
          <!-- module -->
          <template v-if="module">
            <fts-label-group
              :item="module"
              emoji
              :emoji-size="20"
              icon-class="list-nav-module-emoji"
              label-class="list-nav-module-name"
              class="list-nav-module"
            />
            <!-- profile -->
            <div class="list-nav-module-tag" v-if="module.isProfile">
              <font-awesome-icon :icon="['fas', 'user']" fixed-width />
            </div>
            <div class="list-nav-actions">
              <!-- workflow -->
              <fts-tippy content="Open flow diagram" class="list-nav-action">
                <fts-btn
                  slot="trigger"
                  icon
                  text
                  class="list-nav-action"
                  @click="workflow"
                >
                  <font-awesome-icon
                    :icon="['fas', 'project-diagram']"
                    fixed-width
                  />
                </fts-btn>
              </fts-tippy>
              <!-- studio -->
              <fts-tippy
                content="Design"
                class="list-nav-action"
                v-if="isAdminUser"
              >
                <template #trigger> 
                  <router-link :to="studioRoute" custom v-slot="{ href }">
                    <fts-btn
                      tag="a"
                      icon
                      text
                      color="secondary"
                      :href="href"
                    >
                      <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" fixed-width />
                    </fts-btn>
                  </router-link>
                </template>
              </fts-tippy>
            </div>
          </template>
        </div>
        <div class="list-nav-r">
          <div class="list-nav-actions" v-if="listViewLoaded">
            <!-- <fts-basket :selection="basketSelection" class="list-nav-action" /> -->
            <!-- table view -->
            <fts-tippy
              content="list view"
              :options="{ placement: 'bottom-end' }"
              class="list-nav-action"
              :class="{ active: mode === 'list' }"
            >
              <i
                slot="trigger"
                class="fts-icon-list-table fa-lg"
                @click="setListViewMode('list')"
              />
            </fts-tippy>
            <!-- card view -->
            <fts-tippy
              content="card view"
              :options="{ placement: 'bottom-end' }"
              class="list-nav-action"
              :class="{ active: mode === 'card' }"
            >
              <i
                slot="trigger"
                class="fts-icon-list-card fa-lg"
                @click="setListViewMode('card')"
              />
            </fts-tippy>
            <!-- calendar view -->
            <fts-tippy
              content="calendar view"
              :options="{ placement: 'bottom-end' }"
              class="list-nav-action"
              :class="{ active: mode === 'calendar' }"
            >
              <i
                slot="trigger"
                class="fts-icon-calendar fa-lg"
                @click="setListViewMode('calendar')"
              />
            </fts-tippy>
            <!-- user preference -->
            <fts-tippy
              content="user preference"
              :options="{ placement: 'bottom-end' }"
              class="list-nav-action"
            >
              <i
                slot="trigger"
                class="fts-icon-cog fa-lg"
                @click="openUserPreferenceModal"
              />
            </fts-tippy>
          </div>
        </div>
      </div>
      <!-- actions -->
      <div class="list-actions">
        <div class="lv-actions">
          <!-- Create -->
          <fts-btn-hold
            tag="a"
            :href="createActivityUrl"
            color="primary"
            text-transform-init
            box-shadow-none
            class="fts-font-normal"
            v-if="createActivityRight"
            @clickEvent="newLocus"
            @longPressEvent="newLocus(true)"
          >
            <font-awesome-icon :icon="['fas', 'plus']" />New
          </fts-btn-hold>
          <!-- Activity -->
          <fts-btn-hold
            outline
            :color="activity.color ? activity.color : 'primary'"
            :emoji="activity.emoji"
            text-transform-init
            class="btn-activity"
            v-for="activity in activityList"
            :key="activity.id"
            @clickEvent="
              executeActivity(
                activity,
                resultSet.list.filter((x) => x.selected)
              )
            "
            @longPressEvent="
              executeActivity(
                activity,
                resultSet.list.filter((x) => x.selected),
                true
              )
            "
          >
            <span class="btn-activity-label">{{ activity.name }}</span>
          </fts-btn-hold>
          <!-- Custom -->
          <fts-btn
            outline
            :color="action.color ? action.color : 'ternary-blue'"
            :emoji="action.emoji"
            text-transform-init
            class="btn-activity"
            v-for="action in actionList"
            :key="action.id"
            @click="
              executeAction(
                action,
                resultSet.list.filter((x) => x.selected)
              )
            "
          >
            <span :class="{ 'btn-activity-label': !action.color }">{{
              action.name
            }}</span>
          </fts-btn>
          <!-- Document -->
          <fts-btn
            outline
            color="ternary-green"
            text-transform-init
            class="btn-activity"
            v-for="action in documentList"
            :key="action.id"
            @click="
              executeDocument(
                action,
                resultSet.list.filter((x) => x.selected)
              )
            "
          >
            <font-awesome-icon
              :icon="['far', 'file-pdf']"
              class="btn-activity-icon fts-file-pdf"
              v-if="action.typeId === 'pdf'"
            />
            <font-awesome-icon
              :icon="['far', 'file-alt']"
              class="btn-activity-icon fts-file-excel"
              v-else-if="action.typeId === 'spreadsheet'"
            />
            <font-awesome-icon
              :icon="['far', 'file-code']"
              class="btn-activity-icon fts-file-html"
              v-else-if="action.typeId === 'html'"
            />
            <font-awesome-icon
              :icon="['far', 'file']"
              class="btn-activity-icon fts-file-default"
              v-else
            />
            <span class="btn-activity-label">{{ action.name }}</span>
          </fts-btn>
          <!-- Standard Activity -->
          <fts-btn-hold
            outline
            text-transform-init
            class="btn-activity"
            :color="activity.id === 'delete' ? 'danger' : 'primary'"
            v-for="activity in bulkActivities"
            :key="activity.id"
            @clickEvent="
              executeActivity(
                activity,
                resultSet.list.filter((x) => x.selected)
              )
            "
            @longPressEvent="
              executeActivity(
                activity,
                resultSet.list.filter((x) => x.selected),
                true
              )
            "
          >
            <i :class="activity.icon" /> {{ activity.name }}
          </fts-btn-hold>
        </div>
      </div>
    </div>
    <!-- content -->
    <template v-if="listViewLoaded">
      <!-- query -->
      <div class="list-view-query">
        <fts-query-designer
          class="list-view-query-container"
          :filter="filters"
          :states="states"
          :informationSource="filterColumns"
          :mode="'view'"
        />
        <div class="list-view-control">
          <fts-sort :fields="columns" v-model="sort" @closed="retrieveItems" />

          <fts-tippy
            class="list-view-control-icon"
            content="refresh"
            :options="{ placement: 'top-end' }"
          >
            <font-awesome-icon
              :icon="['fas', 'sync-alt']"
              fixed-width
              slot="trigger"
              @click.prevent="retrieveItems"
            />
          </fts-tippy>

          <fts-tippy
            class="list-view-control-icon"
            content="import"
            :options="{ placement: 'top-end' }"
            v-if="moduleImport"
          >
            <font-awesome-icon
              :icon="['fas', 'file-upload']"
              fixed-width
              slot="trigger"
              @click="importData"
            />
          </fts-tippy>

          <fts-tippy
            class="list-view-control-icon"
            content="export"
            :options="{ placement: 'top-end' }"
          >
            <font-awesome-icon
              :icon="['fas', 'file-download']"
              fixed-width
              slot="trigger"
              @click="exportData"
            />
          </fts-tippy>

          <fts-tippy
            class="list-view-control-icon"
            content="add my calendar"
            :options="{ placement: 'top-end' }"
          >
            <font-awesome-icon
              :icon="['far', 'calendar-plus']"
              fixed-width
              slot="trigger"
              @click="openMyCalendarFormModal"
            />
          </fts-tippy>

          <fts-tippy
            class="list-view-control-icon"
            content="bulk actions"
            :options="{ placement: 'top-end' }"
            :class="{ 'control-active': isBulk }"
            v-if="mode === 'list'"
          >
            <font-awesome-icon
              :icon="['fas', 'tasks']"
              fixed-width
              slot="trigger"
              @click="setBulk"
            />
          </fts-tippy>
        </div>
      </div>
      <!-- table view -->
      <div class="list-view-table">
        <fts-table-view
          :bulk="isBulk"
          collaboration
          attachments-row
          editable
          :vector-id="moduleId"
          :list-id="listingId"
          :columns="getColumns"
          :list="formData"
          :standard-actions="header.standards"
          @selectionChanged="updateBasket"
          @activityExecuted="
            executeActivity($event.activity, $event.items, $event.fullView)
          "
          @refresh="retrieveItems"
          v-if="mode == 'list'"
        />
      </div>
      <!-- card view -->
      <fts-card-view
        :list="formData"
        :columns="getColumns"
        :standard-actions="header.standards"
        :action-list="header.customs"
        :activity-list="header.activities"
        :document-list="header.documents"
        :vector-id="moduleId"
        :list-id="listingId"
        @activityExecuted="
          executeActivity($event.activity, $event.items, $event.fullView)
        "
        @actionExecuted="executeAction($event.action, $event.items)"
        @documentExecuted="executeDocument($event.action, $event.items)"
        @refresh="retrieveItems"
        v-if="mode == 'card'"
      />
      <!-- calendar view -->
      <fts-calendar-view
        :listing-id="listingId"
        :module-id="moduleId"
        :list="formData"
        :columns="dateTimeColumns"
        :standard-actions="header.standards"
        @executeActivity="
          executeActivity($event.activity, $event.items, $event.fullView)
        "
        v-if="mode == 'calendar'"
      />
      <!-- not found -->
      <fts-result-not-found
        :show="!loading && formData.length === 0"
        :search="onSearch"
        :creatable="createActivityRight"
        v-if="createActivityRight"
        @create="newLocus"
      />
      <!-- pagination -->
      <fts-pagination
        class="list-view-pagination"
        :total-items="resultSet.totalItems"
        v-model="pageSize"
        @pageChanged="pageChanged"
      />
    </template>
    <!-- spinner -->
    <fts-spinner :show="loading" full-screen boundary="parent" />
    <!-- feedback indicate -->
    <fts-feedback-indicate ref="ftsFeedbackIndicate" />
    <!-- import -->
    <fts-import
      :moduleId="moduleId"
      :moduleImport="moduleImport"
      @importDone="retrieveItems"
      ref="ftsImport"
      v-if="moduleImport"
    />
    <!-- workflow -->
    <fts-workflow-diagram ref="ftsWorkflowDiagram" />
    <!-- document modal -->
    <fts-document-modal ref="documentModal" />
    <!-- document form modal -->
    <fts-document-form-modal
      :moduleId="moduleId"
      :listingId="listingId"
      ref="ftsDocumentFormModal"
    />
    <!-- list view preference modal -->
    <fts-list-view-preference-modal ref="ftsListViewPreferenceModal" />
    <!-- my calendar form modal -->
    <fts-my-calendar-form-modal
      :workspace-id="workspaceId"
      :module-id="moduleId"
      :list-id="listingId"
      :options="dateTimeColumns"
      ref="ftsMyCalendarFormModal" 
    />
  </div>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);

import _ from "lodash";

import { store as canvasDesignStore } from "../store/canvasDesignStore";
import { classificationStore } from "../store/classificationStore";
import { clientStore } from "../store/clientStore";
import { authorizationStore } from "../store/authorizationStore";

import commons from "../commons/commons";
import queryLogic from "../commons/queryLogic";

import FTSTableView from "./views/FTSTableView";
import FTSCardView from "./views/FTSCardView";
import FTSCalendarView from "./views/FTSCalendarView";
import FTSGridView from "./FTSGridView";
import FTSQueryDesigner from "../design/FTSQueryDesigner";
import FTSPagination from "./FTSPagination";
import FTSTippy from "./FTSTippy";
import FTSSpinner from "./FTSSpinner";
import FTSForm from "./FTSForm";
import FTSToggleSwitch from "./FTSToggleSwitch";
import FTSBtnHold from "./FTSBtnHold";
import FTSBtn from "./FTSBtn";
import FTSSort from "./FTSSort";
import FTSImport from "./FTSImport";
import FTSBasket from "./FTSBasket";
import FTSWorkflowDiagram from "./FTSWorkflowDiagram";
import FTSResultNotFound from "./error/FTSResultNotFound";
import FTSFeedbackIndicate from "./FTSFeedbackIndicate";
import FTSLabelGroup from './display/FTSLabelGroup';
import FTSDocumentModal from "./FTSDocumentModal.vue";
import FTSDocumentFormModal from "./FTSDocumentFormModal";
import FTSListViewPreferenceModal from "./FTSListViewPreferenceModal";
import FTSMyCalendarFormModal from "./FTSMyCalendarFormModal";

const listViewMode = [
  { id: "list", code: "l" },
  { id: "card", code: "c" },
  { id: "board", code: "b" },
  { id: "calendar", code: "d" },
];

export default {
  components: {
    "fts-table-view": FTSTableView,
    "fts-card-view": FTSCardView,
    "fts-calendar-view": FTSCalendarView,
    "fts-grid-view": FTSGridView,
    "fts-query-designer": FTSQueryDesigner,
    "fts-pagination": FTSPagination,
    "fts-tippy": FTSTippy,
    "fts-spinner": FTSSpinner,
    "fts-form": FTSForm,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-btn-hold": FTSBtnHold,
    "fts-btn": FTSBtn,
    "fts-sort": FTSSort,
    "fts-import": FTSImport,
    "fts-basket": FTSBasket,
    "fts-workflow-diagram": FTSWorkflowDiagram,
    "fts-result-not-found": FTSResultNotFound,
    "fts-feedback-indicate": FTSFeedbackIndicate,
    "fts-label-group": FTSLabelGroup,
    "fts-document-modal": FTSDocumentModal,
    "fts-document-form-modal": FTSDocumentFormModal,
    "fts-list-view-preference-modal": FTSListViewPreferenceModal,
    "fts-my-calendar-form-modal": FTSMyCalendarFormModal,
  },
  props: {
    baseServer: String,
    propModuleId: [String, Number],
    token: String,
    onUpdated: Function,
    client: {
      type: Boolean,
      default: true,
    },
  },
  store: { classificationStore, canvasDesignStore },
  data: () => ({
    header: {},
    listingId: null,
    moduleId: null,
    moduleImport: null,
    mode: "list",
    isBulk: false,
    vectorName: null,
    columns: [],
    preferences: [],
    filters: { items: [] },
    pageSize: 20,
    page: 0,
    resultSet: { list: [] },
    initialLoad: false,
    // card view
    loading: false,
    onSearch: false,
    scroll: false,
    listViewLoaded: false,
    // sort
    sort: {
      field: null,
      direction: 0,
    },
    sortFilter: null,
    // basket
    basketSelection: [],
  }),
  /**
   * client route parameters
   * id - entry ID
   * l  - listing ID
   * m  - entry view mode
   * a  - activity ID
   * t  - list type
   * d  - document ID
   * v  - vector (module) ID
   * t* - custom params
   * dialog - dialog create form
   * text - dialog create form
   */
  async beforeRouteUpdate(to, from, next) {
    next();
    if (to.fullPath !== from.fullPath) {
      if (!to.query.id && !to.query.a) {
        window.eventBus.$emit("closeQuickView");
      } else {
        var toTrigger = false;
        if (to.query.id && (!from.query || to.query.id != from.query.id)) {
          toTrigger = true;
        } else if (
          to.query.a == "create" && !to.query.dialog &&
          (
            from.query.id ||
            Object.keys(to.query).some(x => x && x.startsWith('t') && x.length > 1)
          )
        ) {
          toTrigger = true;
        } else if (to.query.a == "create") {
          //skip for create
        } else if (to.query.a && (!from.query || to.query.a != from.query.a)) {
          toTrigger = true;
        } else if (to.query.id && to.query.m !== from.query.m) {
          toTrigger = true;
        }

        if (toTrigger) {
          window.eventBus.$emit("quickViewData", {
            id: to.query.id ? commons.decodeids(to.query.id) : null,
            activityId: to.query.a,
            vectorId:
              to.query.a == "create"
                ? commons.decodeids(to.params.moduleId)
                : null,
            fullView: to.query.m && to.query.m.toLowerCase() === "f",
            fromUrl: true,
          });
        }
      }
    }
  },
  watch: {
    filters: {
      deep: true,
      handler: _.debounce(async function (v) {
        this.page = 0;
        await this.retrieveItems();
        try {
          this.onSearch = this.filters.items.some((x) => x.payload);
        } catch (err) {
          console.error(err);
          this.onSearch = true;
        }
      }, 1000),
    },
  },
  methods: {
    onScroll(e) {
      this.scroll = e.target.scrollTop > 0;
    },
    resetScroll() {
      this.$refs.ftsListView.scrollTo({ top: 0, left: 0 });
    },
    getListViewMode() {
      const routeListViewType = listViewMode.find(
        (x) =>
          x.code === this.$route.query.t && this.$route.query.t.toLowerCase()
      );
      if (this.client && routeListViewType && routeListViewType.id)
        this.setListViewMode(routeListViewType.id);
      else if (localStorage[`lv.${this.moduleId}.${this.listingId}`])
        this.setListViewMode(
          localStorage[`lv.${this.moduleId}.${this.listingId}`]
        );
      else this.setListViewMode();
    },
    setListViewMode(type) {
      let mode = "list"; // default
      const listViewType = listViewMode.find((x) => x.id === type);
      if (listViewType) mode = listViewType.id;
      // set mode
      this.mode = mode;
      // set localStorage
      localStorage[`lv.${this.moduleId}.${this.listingId}`] = mode;
      // reset scroll
      this.resetScroll();
      // reset bulk
      if (this.isBulk) this.setBulk();
    },
    submitDocument(action, documentFormData) {
      this.$v.documentFormData.$touch();

      if (this.$v.documentFormData.$invalid) {
        this.$toasted.global.invalidFormValidate();
        return;
      }

      let items = this.resultSet.list.filter((x) => x.selected);
      let payload = {
        document: action,
        items: items,
        moduleId: this.moduleId,
        listingId: this.listingId,
        filters: this.filters,
      };

      if (documentFormData != null) {
        let data = lodash.cloneDeep(documentFormData);
        for (var prop in data) {
          if (prop.indexOf("Model") > 0) {
            delete data[prop];
          }
        }
        parameters.inputs = data;
      }

      // window.eventBus.$emit("showPvDFDocument", payload);

      // let parameters = {
      //   documentId: action.id,
      //   vectorId: this.moduleId,
      //   queryFilters: this.filters,
      // };

      if (action.dataSource === "entry") {
        const entryItems = selectedItems.length ? selectedItems : items;
        if (action.allowSelection) {
          parameters.locusIds = entryItems.map((x) => x.id);
        } else {
          parameters.locusId = entryItems[0].id;
        }
      } else if (action.dataSource == "collection") {
        parameters.list = this.listingId;
      } else {
        //logic service

        parameters.logicService = action.logicServiceName;
        if (documentFormData == null) {
          parameters.inputs = {
            filters: this.filters,
            list: this.listingId,
          };

          if (items != null && items.length > 0) {
            parameters.inputs.id = items[0].id;
          }
        }

        const entryItems = selectedItems.length ? selectedItems : items;
        if (action.allowSelection && entryItems) {
          parameters.inputs.locusIds = entryItems.map((x) => x.id);
        } else if (entryItems && entryItems.length > 0) {
          parameters.inputs.locusId = entryItems[0].id;
        }
      }
      let documentModel = { parameters: parameters };
      classificationStore.dispatch("updateDocumentId", null);
    },
    async pageChanged(page) {
      this.page = page;
      await this.retrieveItems();
      this.resetScroll();
    },
    async retrieveItems() {
      if (this.initialLoad) {
        this.initialLoad = false;
        return;
      }
      this.loading = true;
      try {
        let res = await clientStore.dispatch("loadWorkspaceList", {
          moduleId: this.moduleId,
          listingId: this.listingId,
          pageSize: this.pageSize,
          currentPage: this.page,
          filters: this.filters,
          contextId:
            clientStore.state.workspaceContext != null
              ? clientStore.state.workspaceContext.id
              : null,
          sorts: this.sort.field
            ? [
                {
                  fieldId: this.sort.field.id,
                  direction: this.sort.direction,
                },
              ]
            : null,
        });

        this.resultSet = res.data.data;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    setBulk() {
      this.isBulk = !this.isBulk;
      if (!this.isBulk)
        this.formData.forEach((x) => this.$delete(x, "selected"));
    },
    newLocus(fullView) {
      this.executeActivity({ id: "create" }, [], fullView);
    },
    executeActivity(activity, list, fullView) {
      window.eventBus.$emit("quickViewData", {
        items: list,
        listingId: this.list.id,
        activityId: activity.id,
        vectorId: this.moduleId,
        fullView: fullView,
      });
    },
    executeAction(action, items) {
      eval(action.script);
    },
    async executeDocument(action, items) {
      let vm = this;
      if (action.type == "template") {
        let template = {
          templateId: action.id,
          entryId: items[0].id,
        };

        this.$refs.documentModal.show(template);
      } else if (action.hasForm) {
        let items = this.resultSet.list.filter((x) => x.selected);
        classificationStore.dispatch("updateDocumentId", {
          documentId: action.id,
          vectorId: this.moduleId,
        });
        await this.$refs.ftsDocumentFormModal.show(action, items, this.filters);
        classificationStore.dispatch("updateDocumentId", null);
      } else {
        let parameters = {
          document: action,
          items: items,
          moduleId: this.moduleId,
          listingId: this.listingId,
          filters: this.filters,
        };

        if (action.dataSourceId == "logicService") {
          if (!parameters.inputs) {
            parameters.inputs = {};
          }

          let items = this.resultSet.list.filter((x) => x.selected);
          if (items != null && items.length > 0) {
            parameters.inputs.id = items[0].id;
            parameters.inputs.locusIds = items.map(x => x.id);
          }
        }

        window.eventBus.$emit("showPDFDocument", parameters);
      }
    },
    exportData() {
      const payload = {
        parameters: {
          list: this.listingId,
          vectorId: this.moduleId,
          queryFilters: this.filters,
          context:
            clientStore.state.workspaceContext != null
              ? clientStore.state.workspaceContext.id
              : null,
        },
      };
      const encodedUri = `/service/HTMLTemplateProcessing/document?documentModel=${JSON.stringify(
        payload
      )}`;
      const url = `/#/htmlViewer?file=${encodeURIComponent(encodedUri)}`;
      window.open(url, "_blank");
    },
    importData() {
      this.$refs.ftsImport.open();
    },
    async openUserPreferenceModal() {
      let columns = _.cloneDeep(this.columns);
      if (this.preferences && this.preferences.length) {
        columns = this.preferences.map((x) => {
          var column = this.columns.find((y) => y.id == x.id);
          return { ...x, ...column };
        });
      }

      const result = await this.$refs.ftsListViewPreferenceModal.show(
        columns,
      );

      if (result !== true) {
        await classificationStore.dispatch("saveUserPreference", {
          workspace: this.workspaceName,
          vector: this.moduleId,
          list: this.listingId,
          mode: this.mode,
          columns: result,
        });
        this.preferences = result;
      }
    },
    updateBasket(data) {
      this.basketSelection = data;
    },
    workflow() {
      this.$refs.ftsWorkflowDiagram.show();
    },
    // my calendar
    async openMyCalendarFormModal() {
      this.$refs.ftsMyCalendarFormModal.show();
    },
  },
  computed: {
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    studioRoute() {
      return {
        name: 'studio',
        query: {
          id: commons.encodeids(this.moduleId)
        }, 
      };
    },
    workspaceId() {
      return clientStore.getters.workspaceId;
    },
    workspaceName() {
      return clientStore.getters.workspaceName;
    },
    module() {
      return clientStore.state.workspace.vectors.find(
        (x) => x.id == this.moduleId
      );
    },
    list() {
      return clientStore.state.list;
    },
    states() {
      return clientStore.state.workspace.states.filter(
        (x) => x.module == this.moduleId
      );
    },
    users() {
      return clientStore.state.workspace.users;
    },
    createActivityRight() {
      return (
        this.header &&
        this.header.standards &&
        this.header.standards.indexOf("create") > -1
      );
    },
    createActivityUrl() {
      return commons.clientPathUrl({
        moduleId: this.moduleId,
        listId: this.listingId,
        activity: "create",
      });
    },
    bulkActivities() {
      if (this.isBulk && this.resultSet.list.filter((x) => x.selected).length) {
        let standardActivities = _.intersectionWith(
          commons.standardActivities.filter((x) => x.bulk),
          this.header.standards,
          ({ id }, key) => id === key
        );

        return standardActivities;
      }
      return [];
    },
    activityList() {
      if (this.resultSet && this.header && this.header.activities) {
        const selectedItems = this.resultSet.list.filter((x) => x.selected);
        if (this.mode == "list" && selectedItems.length) {
          return this.header.activities.filter((activity) => {
            if (selectedItems.length > 1 && activity.disableBulkActions)
              return false;
            if (activity.fromStates && activity.fromStates.length) {
              return selectedItems.every((item) =>
                activity.fromStates.some((y) => y == item.state.id)
              );
            }
            return true;
          });
        }
      }
      return [];
    },
    actionList() {
      /* custom visibility */
      if (this.resultSet && this.header && this.header.customs) {
        let items = this.resultSet.list.filter((x) => x.selected);
        return this.header.customs.filter((action) => {
          if (Boolean(action.condition)) {
            try {
              return Boolean(eval(action.condition));
            } catch (err) {
              console.error('[Custom:Condition error]', err);
              return false;
            }
          }

          if (this.mode == "list" && items.length) {
            if (items.length > 1) {
              return action.isMultiple;
            } else {
              return true;
            }
          }

          return action.isDefault;
        });
      }
      return [];
    },
    documentList() {
      if (this.resultSet && this.header && this.header.documents) {
        let items = this.resultSet.list.filter((x) => x.selected);
        return this.header.documents.filter((action) => {
          if (this.mode == "list" && items.length) {
            if (items.length > 1) {
              return action.allowSelection;
            } else {
              return true;
            }
          }

          return (
            action.dataSourceId == "collection" ||
            action.dataSourceId == "logicService"
          );
        });
      }
      return [];
    },
    filterColumns() {
      var filters = [...this.columns];
      /* collaboration */
      filters.splice(1, 0, {
        ...queryLogic.querySelectionPrefix.find(x => x.id === 'collaboration')
      });
      /* assign to */
      const assignToColIndex = filters.findIndex(x => x.id === 'assignTo') + 1;
      if (assignToColIndex)
        filters.splice(
          assignToColIndex,
          0,
          {
            ...queryLogic.querySelectionPrefix.find(x => x.id === 'due')
          }
        );
      /* created date */
      if (!filters.some(x => x.id == 'createdDate')) {
        const createdByColIndex = filters.findIndex(x => x.id === 'createdBy') + 1;
        if (createdByColIndex)
          filters.splice(
            createdByColIndex,
            0,
            {
              ...queryLogic.querySelectionPrefix.find(x => x.id === 'createdDate')
            }
          );
      }
      /* last change date */
      if (!filters.some(x => x.id == 'lastChangeDate')) {
        const lastChangeByColIndex = filters.findIndex(x => x.id === 'lastChangeBy') + 1;
        if (lastChangeByColIndex)
          filters.splice(
            lastChangeByColIndex,
            0,
            {
              ...queryLogic.querySelectionPrefix.find(x => x.id === 'lastChangeDate')
            }
          );
      }

      return filters.filter((x) => {
        if (x.system) {
          return true;
        } else {
          const informationType = commons.informationTypes.find(
            (y) => y.id === x.type
          );
          return informationType && informationType.searchable;
        }
      });
    },
    formData() {
      return this.resultSet.list ? this.resultSet.list : [];
    },
    getColumns() {
      let columns = this.columns;
      if (this.preferences != null && this.preferences.length > 0) {
        columns = this.preferences.map((x) => {
          var column = this.columns.find((y) => y.id == x.id);
          return { ...x, ...column };
        });
      }

      columns.forEach((x) => (delete x.primaryField));

      if (Array.isArray(this.columns)) {
        let filterColumns = columns.filter(x => x.show !== false);
        if (filterColumns.length) filterColumns[0].primaryField = true;
      }

      return columns;
    },
    dateTimeColumns() {
      return this.getColumns
        .filter((x) => (x.type === 5 || x.type === 6) && !x.system)
        .concat([
          {
            id: "createdDate",
            displayName: "Created Date",
            fieldName: "createdDate",
            system: true,
          },
          {
            id: "updatedDate",
            displayName: "Last Change Date",
            fieldName: "updatedDate",
            system: true,
          },
        ]);
    },
  },
  async mounted() {
    this.moduleId = commons.decodeids(this.$route.params.moduleId);
    this.listingId = this.$route.query.l;

    this.loading = true;
    this.listViewLoaded = false;
    // window event bus refresh
    window.eventBus.$on("ftsListViewRefresh", () => {
      this.retrieveItems();
    });
    // workspace module
    try {
      await clientStore.dispatch("loadWorkspaceModule", {
        moduleId: this.moduleId,
        listId: this.listingId,
      });
      // set canvas design, classification users
      canvasDesignStore.dispatch("setUsers", this.users);
      classificationStore.dispatch("setUsers", { users: this.users });

      if (!this.$route.query.dialog) {
        // load list view schema
        try {
          let res = await clientStore.dispatch("loadWorkspaceList", {
            withHeader: true,
            moduleId: this.moduleId,
            listingId: this.listingId,
            pageSize: this.pageSize,
            contextId:
              clientStore.state.workspaceContext != null
                ? clientStore.state.workspaceContext.id
                : null,
          });
          if (res) {
            this.header = res.data.header;
            this.columns = res.data.header.columns;
            this.moduleImport = res.data.import;

            this.initialLoad = true;
            this.resultSet = res.data.data;

            /* preferences columns */
            this.originalPreferences = res.data.header.preferences;
            this.preferences = res.data.header.preferences;
            if (this.preferences && this.preferences.length) {
              const diffColumns = _.differenceBy(this.columns, this.preferences, 'id').map(x => ({ id: x.id }));
              const diffPreferences = _.differenceBy(this.preferences, this.columns, 'id');

              if (diffColumns.length)
                this.preferences = this.preferences.concat(diffColumns);

              if (diffPreferences.length)
                diffPreferences.forEach(x => {
                  this.preferences.splice(this.preferences.indexOf(x), 1);
                });
            }

            // filters
            if (
              res.data.header &&
              res.data.header.defaultFilters &&
              res.data.header.defaultFilters.items &&
              res.data.header.defaultFilters.items.length
            ) {
              this.filters = _.cloneDeep(res.data.header.defaultFilters);
            } else {
              const primaryColumn = this.columns[0];
              this.filters = {
                items: [
                  {
                    id: commons.nanoID(),
                    ...queryLogic.prepareQueryUnit(
                      this.columns,
                      primaryColumn.fieldName
                    ),
                  },
                ],
              };
            }
            this.getListViewMode();
          }

          // retrieve
          window.eventBus.$on("refreshList", () => {
            this.retrieveItems();
          });
          // open quick view
          if (
            this.$route.query.id ||
            (this.$route.query.a === "create" && this.createActivityRight)
          ) {
            window.eventBus.$emit("quickViewData", {
              id: this.$route.query.id
                ? commons.decodeids(this.$route.query.id)
                : null,
              activityId: this.$route.query.a,
              vectorId: this.$route.query.a === "create" ? this.moduleId : null,
              listingId: this.$route.query.l, 
              fullView:
                this.$route.query.m &&
                this.$route.query.m.toLowerCase() === "f",
            });
          }

          this.listViewLoaded = true;
        } catch (err) {
          this.loading = false;
          this.$refs.ftsFeedbackIndicate.show({
            type: "error",
            response: err.response,
            disabledAction: true,
          });
        }
      } else {
        const activity = this.$route.query.a;
        const locusId = commons.decodeids(this.$route.query.id);
        const fullView =
          this.$route.query.m && this.$route.query.m.toLowerCase() === "f";
        if (activity === "create") {
          this.newLocus(fullView);
        } else if (locusId) {
          this.executeActivity(
            { id: "quickView" },
            [{ id: locusId }],
            fullView
          );
        }
        this.listViewLoaded = true;
        this.loading = false;
      }

      this.loading = false;
    } catch (err) {
      this.loading = false;
      this.$refs.ftsFeedbackIndicate.show({
        type: "error",
        response: err.response,
        disabledAction: true,
      });
    }
  },
  beforeDestroy() {
    window.eventBus.$off("ftsListViewRefresh");
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-list-view {
  height: 100%;
  overflow: auto;
  .list-header { 
    @include theme() {
      @include backdrop-background(
        $color: theme-get('fts-background'), 
        $alpha: 0.72, 
        $blur: 20px
      );
    }
    position: sticky;
    top: 0;
    left: 0;
    min-width: 768px;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    color: var(--fts-text);
    z-index: calc(#{$z-index-sticky} + 1);
    &.scrolled {
      box-shadow: 0 0 4px 0 var(--fts-gray-2);
    }
    .list-nav {
      display: flex;
      align-items: center;
      top: 0;
      left: 0;
      height: 55px;
      justify-content: space-between;
      .list-nav-l {
        padding: 0 1rem;
        .list-nav-menu-emoji {
          margin-right: 0.575rem;
        }
        .list-nav-menu-name {
          @include text-overflow-ellipsis;
          font-size: 1.25rem;
          font-weight: 500;
          cursor: default;
        }
      }
      .list-nav-c {
        @include flex-center-middle;
        min-width: 0;
        flex: 1;

        .list-nav-module-emoji {
          margin-right: 0.575rem;
        }
        .list-nav-module-name {
          @include text-overflow-ellipsis;
          font-size: 1.5rem;
          font-weight: 500;
          cursor: default;
        }
        .list-nav-module-tag {
          margin-left: 0.575rem;
          padding: 0.1rem 0.175rem;
          font-size: 0.675rem;
          border-radius: 0.25rem;
          background: var(--drawer-secondary-background);
          color: var(--drawer-secondary);
        }
        .list-nav-actions {
          display: flex;
          align-items: center;
          margin-left: 0.375rem;
        }
      }
      .list-nav-r {
        width: 250px;
        padding: 0 1rem;
        .list-nav-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          margin-left: -0.75rem;
          margin-right: -0.75rem;
        }
        .list-nav-action {
          padding-left: 0.75rem;
          padding-right: 0.75rem;
          color: var(--fts-gray-5);
          cursor: pointer;
          &.active {
            color: $primaryColor;
          }
        }
      }
    }
    .list-actions {
      padding: 1rem;
    }
  }
  /* query */
  .list-view-query {
    position: sticky;
    left: 1rem;
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    margin: 0 1rem 1rem;
    padding: 0.375rem;
    z-index: $z-index-sticky;
    .list-view-query-container {
      flex: 1;
      .query-designer-container {
        .query-designer-row {
          background: initial;
        }
      }
    }
    .list-view-control {
      display: flex;
      align-items: center;
      height: 40px;
      .list-view-control-icon {
        margin-left: 0.75rem;
        font-size: 1.15rem;
        color: var(--fts-gray-5);
        &.control-active {
          color: $primaryColor;
        }
        &:hover {
          cursor: pointer;
        }
      }
    }
  }
  /* list view table */
  .list-view-table {
    margin: 0 1rem 1rem;
  } 
  /* pagination */
  .list-view-pagination {
    margin: 1rem;
  }
}
/* quick view toggle */
.qv-toggle {
  padding: 0.135rem 1rem;
  font-size: 0.875rem;
  a {
    text-decoration: none;
    color: initial;
    span {
      font-weight: bold;
      color: $primaryColor;
    }
  }
}
</style>